import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation, useLocale } from '@parentpath/i18n';
import {
  Users,
  Link as LinkIcon,
  Trash2,
  Loader2,
  Copy,
  Check,
  Share2,
  Lock,
  MessageSquare,
  CreditCard,
  ShieldCheck,
  KeyRound,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useAuth } from '@/features/auth';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { usePartner } from '../hooks/usePartner';
import {
  removePartnerConnection,
  getInviteLink,
  createPartnerInvite,
  acceptPartnerInvite,
  getInviteExpiresAt,
  isInviteExpired,
  ALREADY_CONNECTED,
  formatInviteExpiry,
} from '../services/partnerService';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import { useLimits } from '@/hooks/useLimits';

// Shared Features Info Component
function SharedFeaturesInfo() {
  const { t } = useTranslation('partner');
  return (
    <div className="rounded-[15px] p-4">
      <p className="text-sm text-white/70 leading-relaxed">{t('shared.syncDescription')}</p>
    </div>
  );
}

// Private Features Info Component
function PrivateFeaturesInfo() {
  const { t } = useTranslation('partner');
  return (
    <div className="bg-white/10 rounded-[15px] p-4">
      <h3 className="font-medium text-white mb-3 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-white/25" />
        {t('private.title')}
      </h3>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <MessageSquare className="w-7 h-7 text-white/25 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-white">{t('private.aiAssistant')}</p>
            <p className="text-xs text-white/50">{t('private.aiDescription')}</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Lock className="w-7 h-7 text-white/25 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-white">{t('private.infoCard')}</p>
            <p className="text-xs text-white/50">{t('private.infoCardDescription')}</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <CreditCard className="w-7 h-7 text-white/25 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-white">{t('private.productCheck')}</p>
            <p className="text-xs text-white/50">{t('private.productCheckDescription')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Extract the invite token from a full URL or raw code input.
 * Accepts formats like:
 * - https://app.parentpath.de/invite/abc123def456
 * - parentpath://invite/abc123def456
 * - /invite/abc123def456
 * - abc123def456 (raw token)
 *
 * Bindestriche gehoeren ausdruecklich zum Zeichenvorrat: neue Tokens sind
 * zwar 16 Hex-Zeichen ohne Trennung (generateInviteToken in partnerService),
 * aeltere Einladungen aus dem Onboarding-Pfad tragen aber noch Bindestriche.
 * Ohne sie schnitt das Muster solche Tokens am ersten Bindestrich ab und gab
 * ein Fragment zurueck - der Code wurde dann nicht als ungueltig gemeldet,
 * sondern als falscher Token gesucht und fand nichts. Unterstrich ist
 * mitaufgenommen, damit der Vorrat den ueblichen URL-sicheren Zeichen
 * entspricht und die Pruefung nicht beim naechsten Formatwechsel erneut
 * stillschweigend abschneidet.
 */
function extractInviteToken(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Try to extract token from URL path pattern /invite/{token}.
  // Der Zeichenvorrat endet vor '/', '?' und '#', ein angehaengter
  // Query-String oder Slash bleibt also draussen.
  const match = trimmed.match(/\/invite\/([a-zA-Z0-9_-]+)/);
  if (match) {
    return match[1];
  }

  // If it looks like a plain token (no slashes, no dots, no colons)
  if (/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return trimmed;
  }

  return null;
}

// Invite Code Input Component
function InviteCodeInput({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation('partner');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    const token = extractInviteToken(codeInput);
    if (!token) {
      setError(t('redeemCode.invalidFormat'));
      return;
    }

    setIsSubmitting(true);

    const { error: acceptError } = await acceptPartnerInvite(token);

    if (acceptError) {
      setIsSubmitting(false);
      setError(acceptError.message || t('redeemCode.error'));
      return;
    }

    setIsSubmitting(false);
    setCodeInput('');
    onSuccess();
    navigate('/onboarding');
  };

  return (
    <div className="glass-effect rounded-[15px] p-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-sm text-white/70 hover:text-white transition-colors"
      >
        <span className="flex items-center gap-2">
          <KeyRound className="w-4 h-4" />
          {t('redeemCode.toggle')}
        </span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {isOpen && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-white/50">
            {t('redeemCode.description')}
          </p>
          <input
            type="text"
            value={codeInput}
            onChange={(e) => {
              setCodeInput(e.target.value);
              setError(null);
            }}
            placeholder={t('redeemCode.placeholder')}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                       text-white placeholder-white/40 caret-white text-sm
                       focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                       outline-none transition-all"
          />
          {error && (
            <p className="text-xs text-red-400">{error}</p>
          )}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !codeInput.trim()}
            className="w-full py-3 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('redeemCode.connecting')}
              </>
            ) : (
              t('redeemCode.submit')
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function PartnerPage() {
  const { t } = useTranslation('partner');
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const isPartnerSyncEnabled = useFeatureEnabled('feature_partner_sync');
  const { isLimitEnabled, isLoading: isLimitsLoading, refresh: refreshLimits } = useLimits();
  const isPartnerConnectionAllowed = isLimitEnabled('partner_connection');

  const { user } = useAuth();
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();

  // In baby mode, use lastCompletedPregnancy for partner connection
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const pregnancyId = currentPregnancy?.id || (isBabyMode ? lastCompletedPregnancy?.id : null);

  const {
    partner,
    connection,
    isConnected,
    isPending,
    inviteToken,
    inviteExpiresAt,
    isLoading,
    refetch,
  } = usePartner(pregnancyId);

  const [isRemoving, setIsRemoving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [localInvite, setLocalInvite] = useState<{ token: string; createdAt: string } | null>(null);

  // Sync invite token from hook
  useEffect(() => {
    if (inviteToken && connection?.created_at) {
      setLocalInvite({ token: inviteToken, createdAt: connection.created_at });
    }
  }, [inviteToken, connection?.created_at]);

  const partnerName = partner?.display_name || partner?.email?.split('@')[0] || 'Partner';
  const currentToken = localInvite?.token || inviteToken;
  const inviteLink = currentToken ? getInviteLink(currentToken) : null;
  const expiresAt =
    inviteExpiresAt || (localInvite ? getInviteExpiresAt(localInvite.createdAt) : null);
  // Eine gerade erstellte Einladung kann bei sehr langer Sitzung ablaufen,
  // bevor die Seite neu geladen wird - dann wieder "Einladung erstellen" zeigen.
  const hasValidInvite =
    isPending || (!!localInvite && !isInviteExpired({ created_at: localInvite.createdAt }));

  const [isCancelling, setIsCancelling] = useState(false);

  const handleRemovePartner = async () => {
    if (!connection?.id || !confirm(t('connection.disconnectConfirm'))) return;

    setIsRemoving(true);
    await removePartnerConnection(connection.id);

    // Invalidate subscription cache so shared premium is immediately revoked in UI
    await queryClient.invalidateQueries({ queryKey: ['subscription'] });
    // Refresh limits cache to update feature access
    await refreshLimits();

    await refetch();
    setIsRemoving(false);
  };

  const handleCancelInvite = async () => {
    if (!connection?.id || !confirm(t('cancelInvite.confirm'))) return;

    setIsCancelling(true);
    await removePartnerConnection(connection.id);
    setLocalInvite(null);
    setGenerateError(null);
    await refetch();
    setIsCancelling(false);
  };

  const handleGenerateInvite = async () => {
    if (!pregnancyId || !user) return;

    setIsGenerating(true);
    setGenerateError(null);

    // Existiert bereits eine gültige Einladung, gibt der Service sie zurück,
    // statt eine zweite anzulegen.
    const { data, error } = await createPartnerInvite(pregnancyId, user.id);

    if (error) {
      setGenerateError(
        error.code === ALREADY_CONNECTED
          ? t('errors.alreadyConnected')
          : error.message || t('errors.inviteFailed')
      );
    } else if (data) {
      setLocalInvite({ token: data.invitation_token, createdAt: data.created_at });
    }

    await refetch();
    setIsGenerating(false);
  };

  const handleCodeRedeemed = async () => {
    await queryClient.invalidateQueries({ queryKey: ['subscription'] });
    await refreshLimits();
    await refetch();
  };

  const handleCopyLink = async () => {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!inviteLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('connection.shareTitle'),
          text: t('connection.shareText'),
          url: inviteLink,
        });
      } catch {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  // Show disabled message if feature is turned off
  if (!isPartnerSyncEnabled) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  // Show premium upgrade message if partner connection not allowed for user's tier
  if (!isLimitsLoading && !isPartnerConnectionAllowed && !isConnected) {
    return (
      <div className="space-y-6 pb-8">
        <h1 className="text-2xl font-bold text-white">{t('pageTitle')}</h1>
        <div className="glass-effect rounded-[15px] p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-pp-orange" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">
              {t('premium.title')}
            </h2>
            <p className="text-white/60 mb-4">
              {t('premium.description')}
            </p>
            <Link
              to="/subscription"
              className="inline-block py-3 px-6 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors"
            >
              {t('premium.upgrade')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || isLimitsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <h1 className="text-2xl font-bold text-white">{t('pageTitle')}</h1>

      {/* No Pregnancy Yet */}
      {!pregnancyId && (
        <div className="glass-effect rounded-[15px] p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-white/40" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">
              {t('noPregnancy.title')}
            </h2>
            <p className="text-white/60 mb-4">
              {t('noPregnancy.description')}
            </p>
            <Link
              to="/onboarding"
              className="inline-block py-3 px-6 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors"
            >
              {t('noPregnancy.setupPregnancy')}
            </Link>
          </div>
        </div>
      )}

      {/* Connected Partner */}
      {pregnancyId && isConnected && partner && (
        <>
          <div className="bg-white/10 rounded-[15px] p-4 flex items-center gap-4">
            <LinkIcon className="w-5 h-5 text-white/50 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-white">{partnerName}</h2>
              <p className="text-sm text-white/50">{partner.email}</p>
            </div>
          </div>

          {/* Shared Features Info */}
          <SharedFeaturesInfo />

          {/* Private Features Info */}
          <PrivateFeaturesInfo />

          {/* Remove Partner */}
          <div className="rounded-[15px] p-4">
            <h3 className="font-medium text-white mb-2">{t('remove.title')}</h3>
            <p className="text-sm text-white/60 mb-4">
              {t('remove.description')}
            </p>
            <button
              onClick={handleRemovePartner}
              disabled={isRemoving}
              className="py-3 px-4 glass-effect border border-red-500/30 text-red-400 font-medium rounded-[15px] hover:bg-red-500/10 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isRemoving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {t('remove.button')}
            </button>
          </div>
        </>
      )}

      {/* Pending Invite */}
      {pregnancyId && hasValidInvite && inviteLink && (
        <>
          <div className="bg-white/10 rounded-[15px] p-4 border border-pp-orange/30">
            <div className="flex items-center gap-2 font-semibold text-white mb-3">
              <Users className="w-5 h-5 text-white/50" />
              {t('connection.invitePending')}
            </div>
            <p className="text-sm text-white/60 mb-4">
              {t('connection.shareWithPartner')}
            </p>
            <div className="glass-effect rounded-[10px] p-3 mb-3">
              <p className="text-sm text-white/70 font-mono break-all">{inviteLink}</p>
            </div>
            {expiresAt && (
              <p className="text-xs text-white/50 mb-4">
                {t('connection.validUntil', { date: formatInviteExpiry(expiresAt, locale) })}
              </p>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleCopyLink}
                className="flex-1 py-3 glass-effect border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? t('connection.copied') : t('connection.copy')}
              </button>
              <button
                onClick={handleShare}
                className="flex-1 py-3 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                {t('connection.share')}
              </button>
            </div>
          </div>

          {/* Cancel Invitation */}
          <div className="rounded-[15px] p-4">
            <h3 className="font-medium text-white mb-2">{t('cancelInvite.title')}</h3>
            <p className="text-sm text-white/60 mb-4">
              {t('cancelInvite.description')}
            </p>
            <button
              onClick={handleCancelInvite}
              disabled={isCancelling}
              className="py-3 px-4 text-red-400 font-medium rounded-[15px] hover:bg-red-500/10 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isCancelling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {t('cancelInvite.button')}
            </button>
          </div>

          {/* Shared Features Info */}
          <SharedFeaturesInfo />

          {/* Private Features Info */}
          <PrivateFeaturesInfo />
        </>
      )}

      {/* No Partner - Generate Invite */}
      {pregnancyId && !isConnected && !hasValidInvite && (
        <>
          <div className="bg-white/10 rounded-[15px] p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white/50" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">
                {t('connection.title')}
              </h2>
              <p className="text-white/60 mb-4">
                {t('connection.description')}
              </p>
              <button
                onClick={handleGenerateInvite}
                disabled={isGenerating}
                className="py-3 px-6 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('connection.generating')}
                  </>
                ) : (
                  t('connection.generateInvite')
                )}
              </button>
              {generateError && (
                <p className="text-sm text-red-400 mt-3">{generateError}</p>
              )}
            </div>
          </div>

          {/* Redeem Invite Code */}
          {user && (
            <InviteCodeInput onSuccess={handleCodeRedeemed} />
          )}

          {/* Shared Features Info */}
          <SharedFeaturesInfo />

          {/* Private Features Info */}
          <PrivateFeaturesInfo />
        </>
      )}
    </div>
  );
}
